/**
 * Composant d'affichage des codes de backup 2FA
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Affiche les codes de backup avec options de copie et téléchargement.
 */

  // Client Component pour Next.js

import { useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Props du composant BackupCodesDisplay
 */
export interface BackupCodesDisplayProps {
  /** Liste des codes de backup */
  codes: string[];
  /** Callback après copie */
  onCopy?: () => void;
  /** Callback après téléchargement */
  onDownload?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant BackupCodesDisplay
 *
 * @example
 * ```tsx
 * function TwoFactorActivation() {
 *   const { activate2FA, backupCodes } = use2FA();
 *
 *   return (
 *     <div>
 *       {backupCodes.length > 0 && (
 *         <BackupCodesDisplay codes={backupCodes} />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function BackupCodesDisplay({
  codes,
  onCopy,
  onDownload,
  className = '',
}: BackupCodesDisplayProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  /**
   * Copier un code individuel
   */
  const copyCode = async (code: string, index: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      toast.error('Impossible de copier le code dans le presse-papiers');
    }
  };

  /**
   * Copier tous les codes
   */
  const copyAllCodes = async () => {
    try {
      const allCodes = codes.join('\n');
      await navigator.clipboard.writeText(allCodes);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
      onCopy?.();
    } catch (err) {
      toast.error('Impossible de copier les codes dans le presse-papiers');
    }
  };

  /**
   * Télécharger les codes en fichier texte
   */
  const downloadCodes = () => {
    const content = `Codes de backup - Authentification à deux facteurs\n\nGénérés le: ${new Date().toLocaleString('fr-FR')}\n\n${codes.join('\n')}\n\nATTENTION: Conservez ces codes en lieu sûr. Chaque code ne peut être utilisé qu'une seule fois.`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup-codes-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    onDownload?.();
  };

  if (codes.length === 0) {
    return null;
  }

  return (
    <div className={`bg-surface rounded-lg border border-border p-6 ${className}`}>
      {/* Alerte d'avertissement */}
      <div className="mb-4 p-4 bg-warning-light border border-warning rounded-lg">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-warning mt-0.5 mr-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-warning">
              Important - Sauvegardez ces codes
            </h3>
            <p className="mt-1 text-xs text-warning">
              Ces codes de backup vous permettront de vous connecter si vous perdez l'accès à votre authentification à deux facteurs.
              Chaque code ne peut être utilisé qu'une seule fois. Conservez-les en lieu sûr.
            </p>
          </div>
        </div>
      </div>

      {/* Liste des codes */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-content mb-3">
          Vos codes de backup ({codes.length} codes)
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {codes.map((code, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-surface-raised rounded-md px-3 py-2 font-mono text-sm"
            >
              <span className="text-content select-all">
                {code}
              </span>
              <button
                type="button"
                onClick={() => copyCode(code, index)}
                className="ml-2 p-1 text-content-subtle hover:text-content transition-colors"
                title="Copier ce code"
                aria-label={`Copier le code ${index + 1}`}
              >
                {copiedIndex === index ? (
                  <svg className="w-4 h-4 text-success" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={copyAllCodes}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-border rounded-md shadow-sm text-sm font-medium text-content-muted bg-surface hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
        >
          {copiedAll ? (
            <>
              <svg className="w-4 h-4 mr-2 text-success" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Copié !
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copier tous les codes
            </>
          )}
        </button>

        <button
          type="button"
          onClick={downloadCodes}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-content-inverted bg-accent hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Télécharger (.txt)
        </button>
      </div>
    </div>
  );
}
